import * as React from "react";
import {
	Box,
	Button,
	Chip,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import {
	CheckCircleIcon,
	CurrencyCircleDollarIcon,
	PencilSimpleIcon,
	PlusIcon,
	TrashIcon,
} from "@phosphor-icons/react";

import type { PriceBookItem, PriceBookPayload } from "@/hooks/api/use-campaign-requests";

import { marketFlag, marketLabel, PRICE_CODE_LABELS, PRICE_CODE_UNIT_HINTS } from "../constants";
import { labelOf } from "../humanise";
import { RateEditRow } from "./rate-edit-row";
import { formatRate, isUnagreedRate, type RateRow } from "./rate-rows";

export interface RatesTableProps {
	rows: RateRow[];
	/** The account being read, in words. Null means the platform defaults are being read directly. */
	accountId: string | null;
	accountName: string | null;
	isLoading: boolean;
	emptyText: string;
	/** The rate currently open for inline editing, if any. */
	editingKey: string | null;
	isSaving: boolean;
	onEdit: (row: RateRow, item: PriceBookItem) => void;
	onCancelEdit: () => void;
	onSaveEdit: (payload: PriceBookPayload) => void;
	/** Create the rate this row is missing, or the account override it does not have yet. */
	onSet: (row: RateRow) => void;
	onConfirm: (item: PriceBookItem) => void;
	onDelete: (row: RateRow, item: PriceBookItem) => void;
}

const SOURCE_LABELS: Record<RateRow["source"], string> = {
	override: "Override",
	override_all_markets: "Override · all markets",
	platform: "Platform default",
	platform_all_markets: "Platform default · all markets",
	unset: "Not set",
};

/** Amber, and loud about it. A rate nobody agreed is the one thing this screen exists to catch. */
function blockedSx(blocked: boolean) {
	return blocked
		? {
				bgcolor: "var(--mui-palette-warning-selected)",
				"& > td:first-of-type": { boxShadow: "inset 3px 0 0 0 var(--mui-palette-warning-main)" },
			}
		: undefined;
}

/**
 * One word per row, and the word is the whole status.
 *
 * These used to be sentences — "Not confirmed — blocks quoting" — with the rest of the
 * explanation on a hover. Both are wrong here: the sentence is the same sentence on every amber
 * row, and a hover is not where an operator finds out why half the queue is stuck. The words are
 * said once, above the table, in {@link RatesLegend}; the cell is the badge.
 */
function StatusCell({ row }: { row: RateRow }): React.JSX.Element {
	if (row.missing) return <Chip color="warning" variant="soft" size="small" label="Not set" />;

	if (row.unconfirmed) return <Chip color="warning" variant="soft" size="small" label="Unconfirmed" />;

	// Configurable but never resolved by the pricer, so its absence is not a gap. Amber here would
	// only teach the operator to ignore the amber that does stop a quote.
	if (!row.effective) return <Chip variant="outlined" size="small" label="Not set · optional" />;

	return <Chip color="success" variant="soft" size="small" label="Confirmed" />;
}

/*
 * `RatesLegend` lived here: three badges, each with a paragraph defining it, above a table whose
 * badges read "Confirmed", "Unconfirmed" and "Not set". Deleted — a glossary for three words is
 * documentation, and the counts it carried are already chips at the top of the screen.
 */

/**
 * The rates in force, and where each one comes from.
 *
 * Two things are deliberately visible rather than resolved away. A rate nobody has agreed is drawn
 * amber and badged "Unconfirmed" whichever wording its note uses — what that costs is one of two
 * failures, and the legend above the table says which — and an account's overrides are shown
 * beside the platform default they replace, because a negotiated rate read out of context is
 * indistinguishable from a mistake.
 */
export function RatesTable({
	rows,
	accountId,
	accountName,
	isLoading,
	emptyText,
	editingKey,
	isSaving,
	onEdit,
	onCancelEdit,
	onSaveEdit,
	onSet,
	onConfirm,
	onDelete,
}: RatesTableProps): React.JSX.Element {
	const showAccountColumn = Boolean(accountId);
	/**
	 * Whether anybody has written a note.
	 *
	 * The seeded "nobody has agreed this" text is read by the Status badge, not printed here, so
	 * on a fresh book this column is eleven em-dashes wide enough to hold 240px of nothing.
	 */
	const showNoteColumn = rows.some((row) => Boolean(row.note));
	// Charge and market are rendered by the edit row itself; the actions cell is its own. What is
	// left in between is what the inline form spans.
	const fieldColSpan = 2 + (showAccountColumn ? 1 : 0) + (showNoteColumn ? 1 : 0);

	if (isLoading) {
		return (
			<Stack spacing={1} sx={{ p: 2 }}>
				{[0, 1, 2, 3, 4].map((line) => (
					<Skeleton key={line} variant="rectangular" height={44} />
				))}
			</Stack>
		);
	}

	if (rows.length === 0) {
		return (
			<Box sx={{ p: 3 }}>
				<Stack
					spacing={1}
					sx={{
						alignItems: "center",
						border: "1px dashed var(--mui-palette-divider)",
						borderRadius: 1,
						px: 3,
						py: 5,
						textAlign: "center",
					}}
				>
					<CurrencyCircleDollarIcon size={28} color="var(--mui-palette-text-disabled)" />
					<Typography variant="subtitle2">No rates here yet</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
						{emptyText}
					</Typography>
				</Stack>
			</Box>
		);
	}

	return (
		<Box sx={{ overflowX: "auto" }}>
			<Table size="small" sx={{ minWidth: 940 }}>
				<TableHead>
					<TableRow>
						<TableCell sx={{ fontWeight: 600 }}>Charge</TableCell>
						<TableCell sx={{ fontWeight: 600 }}>Market</TableCell>
						{/* No Unit column: "Per minute" repeated the "per minute · billed per second"
						    line already under the charge name. No Currency column either — the amount
						    beside it now carries its own code. */}
						<TableCell sx={{ fontWeight: 600 }} align="right">
							Platform Default
						</TableCell>
						{showAccountColumn ? (
							<TableCell sx={{ fontWeight: 600 }} align="right">
								{accountName ?? "This account"}
							</TableCell>
						) : null}
						<TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
						{showNoteColumn ? <TableCell sx={{ fontWeight: 600 }}>Note</TableCell> : null}
						<TableCell sx={{ fontWeight: 600 }} align="right">
							Actions
						</TableCell>
					</TableRow>
				</TableHead>

				<TableBody>
					{rows.map((row) => {
						// The rate this view is allowed to edit: an account view edits that account's own
						// rates, never the platform's. Changing what every workspace pays is a different
						// decision and lives on a different screen state.
						const scoped = accountId ? row.override : row.platform;
						const effective = row.effective;
						// Amber follows the badge: a rate nobody agreed and a rate nobody set both need
						// an answer, whether or not the server's own marker recognises this one.
						const blocked = row.unconfirmed || row.missing;

						if (scoped && editingKey === row.key) {
							return (
								<RateEditRow
									key={row.key}
									item={scoped}
									scopeLabel={accountId ? `${accountName ?? "this account"}'s rate` : "the platform default"}
									fieldColSpan={fieldColSpan}
									isSaving={isSaving}
									onCancel={onCancelEdit}
									onSave={onSaveEdit}
								/>
							);
						}

						return (
							<TableRow key={row.key} sx={blockedSx(blocked)}>
								<TableCell>
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{labelOf(PRICE_CODE_LABELS, row.code)}
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{PRICE_CODE_UNIT_HINTS[row.code]}
									</Typography>
								</TableCell>

								<TableCell>
									{row.market ? (
										<Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
											<span>{marketFlag(row.market)}</span>
											<span>{marketLabel(row.market)}</span>
										</Stack>
									) : (
										<Typography variant="body2" color="text.secondary">
											All markets
										</Typography>
									)}
								</TableCell>

								<TableCell align="right">
									{row.platform ? (
										<Stack spacing={0.25} sx={{ alignItems: "flex-end" }}>
											<Typography variant="body2">{formatRate(row.platform.amount, row.platform.currency)}</Typography>
											{/* Only where the account's own rate is what this row resolves to: otherwise
											    the Status badge is already saying this about the same number. */}
											{row.override && isUnagreedRate(row.platform.note) ? (
												<Typography variant="caption" color="warning.main">
													unconfirmed
												</Typography>
											) : null}
										</Stack>
									) : (
										<Typography variant="body2" color={row.missing ? "warning.main" : "text.secondary"}>
											Not set
										</Typography>
									)}
								</TableCell>

								{showAccountColumn ? (
									<TableCell align="right">
										{row.override ? (
											<Stack spacing={0.5} sx={{ alignItems: "flex-end" }}>
												<Typography variant="body2" color="primary.main" sx={{ fontWeight: 700 }}>
													{formatRate(row.override.amount, row.override.currency)}
												</Typography>
												<Chip size="small" color="primary" variant="soft" label="Override" />
											</Stack>
										) : row.effective ? (
											// The chip already says where the number came from — "Platform default · all
											// markets" is the whole sentence the hover used to spell out.
											<Stack spacing={0.5} sx={{ alignItems: "flex-end" }}>
												<Typography variant="body2" color="text.secondary">
													{formatRate(row.effective.amount, row.effective.currency)}
												</Typography>
												<Chip size="small" variant="outlined" label={SOURCE_LABELS[row.source]} />
											</Stack>
										) : (
											<Typography variant="body2" color={row.missing ? "warning.main" : "text.secondary"}>
												Not set
											</Typography>
										)}
									</TableCell>
								) : null}

								<TableCell>
									<StatusCell row={row} />
								</TableCell>

								{/* Only what somebody wrote, and only when somebody wrote it. The stored
								    note itself is untouched — that is what the server matches on. */}
								{showNoteColumn ? (
									<TableCell sx={{ maxWidth: 240 }}>
										{row.note ? (
											<Typography variant="caption" color="text.secondary">
												{row.note}
											</Typography>
										) : (
											<Typography variant="caption" color="text.disabled">
												—
											</Typography>
										)}
									</TableCell>
								) : null}

								{/* Labelled rather than iconographic: three icon buttons meant three tooltips,
								    and a destructive action behind a hover is the wrong place for it. */}
								<TableCell align="right">
									<Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
										{row.unconfirmed && effective ? (
											<Button
												size="small"
												color="warning"
												variant="outlined"
												startIcon={<CheckCircleIcon />}
												onClick={() => onConfirm(effective)}
											>
												Confirm
											</Button>
										) : null}

										{scoped ? (
											<React.Fragment>
												<Button
													size="small"
													color="secondary"
													startIcon={<PencilSimpleIcon />}
													onClick={() => onEdit(row, scoped)}
												>
													Edit
												</Button>
												<Button
													size="small"
													color="error"
													startIcon={<TrashIcon />}
													onClick={() => onDelete(row, scoped)}
												>
													Remove
												</Button>
											</React.Fragment>
										) : (
											<Button
												size="small"
												variant={blocked ? "contained" : "outlined"}
												color={blocked ? "warning" : "primary"}
												startIcon={<PlusIcon />}
												onClick={() => onSet(row)}
											>
												{accountId ? "Override" : "Set"}
											</Button>
										)}
									</Stack>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>
		</Box>
	);
}
